import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/src/query';
import { env } from 'next-runtime-env';

import { retentionTags } from '~shared/api/retention/names';

import type {
	RetentionBannerPlacement,
	RetentionBannerResponse,
	RetentionPopupResponse,
} from './types';

const liveApiFallback = 'https://api.winemback.com/';

const retentionBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
	return fetchBaseQuery({
		baseUrl: env('NEXT_PUBLIC_RETENTION_API') ?? liveApiFallback,
	})(args, api, extraOptions);
};

export const retentionApi = createApi({
	reducerPath: 'retentionApi',
	baseQuery: retentionBaseQuery,
	endpoints: (build) => ({
		popupConfig: build.query<
			RetentionPopupResponse,
			{ userId: string; jwt: string }
		>({
			query: ({ userId, jwt }) => ({
				url: `/contacts/${userId}/popup/messages`,
				method: 'GET',
				headers: {
					Authorization: `External jwt="${jwt}"`,
				},
			}),
			providesTags: (_, __, arg) => [
				{ type: retentionTags.popup, id: arg.userId },
			],
		}),
		deleteConfig: build.mutation<unknown, { userId: string; jwt: string }>({
			query: ({ userId, jwt }) => ({
				url: `/contacts/${userId}/popup/messages`,
				method: 'DELETE',
				headers: {
					Authorization: `External jwt="${jwt}"`,
				},
			}),
			invalidatesTags: (_, __, args) => [
				{ type: retentionTags.popup, id: args.userId },
			],
		}),
		bannersConfig: build.mutation<
			RetentionBannerResponse,
			{
				userId: string;
				jwt: string;
				email?: string;
				placement: RetentionBannerPlacement;
				attempt?: number;
			}
		>({
			query: ({ userId, jwt, email, placement }) => {
				const data: Record<string, string> = {};
				if (email) {
					data['email'] = email;
				}

				return {
					url: `/contacts/${userId}/banners`,
					method: 'POST',
					headers: {
						Authorization: `External jwt="${jwt}"`,
					},
					body: {
						placement,
						forceRender: false,
						contact: {},
						data,
					},
				};
			},
			async onQueryStarted(
				{ userId, jwt, email, placement, attempt },
				{ dispatch, queryFulfilled }
			) {
				try {
					const { data: res } = await queryFulfilled;
					if (res.status === 'inprogress' && (attempt || 0) < 3) {
						void dispatch(
							retentionApi.endpoints.bannersConfig.initiate(
								{
									userId,
									jwt,
									email,
									placement,
									attempt: (attempt || 0) + 1,
								},
								{
									fixedCacheKey: `getBanner${placement}`,
								}
							)
						);
					}
				} catch {}
			},
		}),
	}),
	tagTypes: [retentionTags.popup],
});
